import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import apiClient, { getApiUrl } from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
// formatIndiaTime and getIndiaTime removed - using formatLocalTime instead
import { ChatSkeleton, ChatMessagesSkeleton } from '../components/Skeleton';
import { MessageCircle, Search, Send, RefreshCw, FileText, ChevronDown, X, Check, CheckCheck, Paperclip, ArrowLeft } from 'lucide-react';

// Chat list helpers
const getChatLatestTimestamp = (chat) => {
  const fromObj = chat.lastMessageObj?.created_at;
  if (fromObj) {
    const t = new Date(fromObj).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (chat.messages?.length) {
    const times = chat.messages.map(m => new Date(m.created_at).getTime()).filter(t => !Number.isNaN(t));
    if (times.length) return Math.max(...times);
  }
  return 0;
};

const normalizePhone = (phone) => {
  if (!phone) return null;
  // Remove all non-numeric characters and get last 10 digits
  const cleaned = phone.replace(/[^0-9]/g, '');
  return cleaned.slice(-10);
};

const isTempMessageId = (id) => String(id || '').startsWith('tmp-');

// DB stores MySQL datetimes in IST; ISO strings from API/optimistic may be UTC
const parseMessageTimestamp = (dateString) => {
  if (!dateString) return NaN;
  if (typeof dateString === 'string') {
    if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      return new Date(dateString).getTime();
    }
    const dateTimeStr = dateString.replace(' ', 'T');
    const isoString = dateTimeStr.includes('.') ? `${dateTimeStr}+05:30` : `${dateTimeStr}.000+05:30`;
    return new Date(isoString).getTime();
  }
  return new Date(dateString).getTime();
};

const messagesMatchPhones = (a, b) => {
  const fromA = normalizePhone(a.from_number || '') || '';
  const toA = normalizePhone(a.to_number || '') || '';
  const fromB = normalizePhone(b.from_number || '') || '';
  const toB = normalizePhone(b.to_number || '') || '';
  if (fromA === fromB && toA === toB) return true;
  if (fromA === toB && toA === fromB) return true;
  // Outgoing optimistic messages often have null from_number
  if (!fromA || !fromB) return toA === toB || fromA === fromB;
  return false;
};

const messagesMatchContent = (a, b) => {
  const aMedia = a.media_url || '';
  const bMedia = b.media_url || '';
  if (aMedia && bMedia) return aMedia === bMedia;
  const aText = (a.message_text || '').trim();
  const bText = (b.message_text || '').trim();
  return Boolean(aText && bText && aText === bText && !aMedia && !bMedia);
};

const areMessagesDuplicate = (a, b, timeToleranceMs = 15000) => {
  if (!a || !b) return false;
  const aId = a.id;
  const bId = b.id;
  if (aId && bId && !isTempMessageId(aId) && !isTempMessageId(bId) && String(aId) === String(bId)) {
    return true;
  }
  if (!messagesMatchContent(a, b) || !messagesMatchPhones(a, b)) return false;
  const ta = parseMessageTimestamp(a.created_at);
  const tb = parseMessageTimestamp(b.created_at);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return true;
  return Math.abs(ta - tb) < timeToleranceMs;
};

const dedupeMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const result = [];
  for (const msg of messages) {
    const existingIdx = result.findIndex((existing) => areMessagesDuplicate(existing, msg));
    if (existingIdx === -1) {
      result.push(msg);
      continue;
    }
    const existing = result[existingIdx];
    const preferNew =
      (msg.id && !isTempMessageId(msg.id) && isTempMessageId(existing.id)) ||
      (msg.id && !existing.id) ||
      (parseMessageTimestamp(msg.created_at) >= parseMessageTimestamp(existing.created_at));
    if (preferNew) result[existingIdx] = msg;
  }
  return result.sort((a, b) => parseMessageTimestamp(a.created_at) - parseMessageTimestamp(b.created_at));
};

const getMessageRenderKey = (msg, fallback) => {
  if (msg.id && !isTempMessageId(msg.id)) return `msg-${msg.id}`;
  return fallback;
};

// Format time in user's local timezone
// The database stores timestamps in UTC (UTC+0)
// We need to parse them as UTC and let JavaScript automatically convert to local timezone
const formatLocalTime = (dateString, format = 'time-ampm') => {
  if (!dateString) return '';

  let date;

  // Handle different timestamp formats from the database
  if (typeof dateString === 'string') {
    // If it includes timezone info (Z or +00:00, +05:30, etc.), parse as-is
    if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      date = new Date(dateString);
    } else {
      // Plain datetime string from database (e.g., '2025-01-15 14:30:00')
      // Database stores in UTC, so we need to parse it as UTC
      // Convert to ISO format and append 'Z' to indicate UTC
      const dateTimeStr = dateString.replace(' ', 'T');
      // If it doesn't have seconds/milliseconds, add them
      const isoString = dateTimeStr.includes('.') ? dateTimeStr + 'Z' : dateTimeStr + '.000Z';
      date = new Date(isoString);
    }
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) {
    // Fallback: try parsing as-is (might be a Date object or other format)
    date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
  }

  // Date header for chat separators (Today, Yesterday, or date - like WhatsApp)
  if (format === 'date-header') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const messageDate = new Date(date);
    messageDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - messageDate) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';

    return messageDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }

  if (format === 'relative') {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) {
      const hours = diffHours;
      const mins = diffMins % 60;
      return mins > 0 ? `${hours}h ${mins}m ago` : `${hours}h ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    // Show date for older messages - use local date comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const messageDate = new Date(date);
    messageDate.setHours(0, 0, 0, 0);
    const diffDaysLocal = Math.floor((today - messageDate) / 86400000);

    if (diffDaysLocal === 0) return 'Today';
    if (diffDaysLocal === 1) return 'Yesterday';

    return messageDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }

  if (format === 'time-ampm') {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  if (format === 'time-only') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  // Default: full date and time
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  });
};

const Chats = () => {
  const { currentWorkspace } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Debug: Log workspace on mount only (avoid log spam during realtime updates)
  const [filteredChats, setFilteredChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendInProgress, setSendInProgress] = useState(false); // Disables button (no loader for optimistic)
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const processedContactRef = useRef(null);
  const selectedChatPhoneRef = useRef(null); // track selected chat phone for open-from-Contacts message fetch
  // Suppress SSE updates for a chat right after we manually refresh it (to avoid duplicates)
  const sseIgnorePhoneRef = useRef(null);
  const sseIgnoreTimeoutRef = useRef(null);
  const blobUrlsToRevokeRef = useRef([]);
  const handleChatSelectRef = useRef(null);
  const contactQueryParam = searchParams.get('contact');
  const contactNameParam = searchParams.get('name');
  const contactOpenToken = searchParams.get('_open');
  const openContactFromState = location.state?.openContact;
  const contactNameFromState = location.state?.contactName;

  // Message pagination for chat history
  const MESSAGE_PAGE_SIZE = 50;
  const [messagePages, setMessagePages] = useState(1);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const messageApiPageRef = useRef(1);
  const [hasMoreOlderMessages, setHasMoreOlderMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Only show the most recent N messages (pages * 50), oldest -> newest
  const visibleMessages = useMemo(() => {
    if (!messages || messages.length === 0) return [];
    const limit = MESSAGE_PAGE_SIZE * messagePages;
    const startIndex = Math.max(0, messages.length - limit);
    return messages.slice(startIndex);
  }, [messages, messagePages, MESSAGE_PAGE_SIZE]);

  // Pagination and filtering state
  const [chatPage, setChatPage] = useState(1); // API page for chats (10 per page)
  const [currentPage, setCurrentPage] = useState(1); // UI page within filtered list
  const [chatsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showActiveRecentOnly, setShowActiveRecentOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Mobile view state - show list on mobile, both on desktop
  const [showMobileChat, setShowMobileChat] = useState(false);

  // Template-related state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [templateParams, setTemplateParams] = useState({});
  const [useTemplate, setUseTemplate] = useState(false);

  // Media upload state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const fileInputRef = useRef(null);


  // 24-hour window state
  const [windowStatus, setWindowStatus] = useState({
    isActive: false,
    lastClientMessageAt: null,
    canSendDirect: false,
    canSendFacebookTemplate: true
  });

  const [syncingTemplates, setSyncingTemplates] = useState(false);

  // Debounce search so we don't hit the server on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 400); // wait 400ms after user stops typing
    return () => clearTimeout(handle);
  }, [searchInput]);

  // When any filter is active, API fetches more and filters server-side; use page 1
  const hasActiveFilters = !!(searchTerm?.trim() || selectedGroup || selectedTag || showActiveRecentOnly);
  const apiPage = hasActiveFilters ? 1 : chatPage;

  // Fetch chats - server applies search/group/tag/unread filters when provided
  const { data: chatsData, isLoading: loading } = useQuery(
    ['chats', currentWorkspace?.id, apiPage, searchTerm, selectedGroup, selectedTag, showActiveRecentOnly],
    async () => {
      if (!currentWorkspace?.id) return { chats: [], pagination: { page: 1, limit: 10, total: 0, pages: 1 }, activeRecentCount: 0 };

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const params = { page: apiPage, limit: 10, messagesPerChat: 50 };
      if (searchTerm?.trim()) params.search = searchTerm.trim();
      if (selectedGroup) params.group = selectedGroup;
      if (selectedTag) params.tag = selectedTag;
      if (showActiveRecentOnly) params.activeRecentOnly = 'true';

      const response = await apiClient.get('/api/whatsapp/chats', { headers, params });

      const rawChats = response.data?.chats || [];
      const chats = rawChats.map(chat => ({
        ...chat,
        messages: [...(chat.messages || [])].reverse()
      }));
      return {
        chats,
        pagination: response.data?.pagination || { page: 1, limit: 10, total: 0, pages: 1 },
        activeRecentCount: response.data?.activeRecentCount ?? 0
      };
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error loading chats:', error);
        toast.error('Failed to load chats');
      }
    }
  );

  // Lazy-load templates when user opens template dropdown
  const { data: templatesData } = useQuery(
    ['chat-templates', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return { facebookTemplates: [] };
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const facebookRes = await apiClient
        .get('/api/whatsapp/templates?days=365&useCache=true', { headers })
        .catch(() => ({ data: { templates: [] } }));
      return {
        facebookTemplates: facebookRes.data?.templates || []
      };
    },
    { enabled: !!currentWorkspace?.id && showTemplateDropdown, staleTime: 5 * 60 * 1000 }
  );

  // Lazy-load groups and tags when user opens filter dropdown
  const { data: filterData } = useQuery(
    ['chat-filters', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return { groups: [], tags: [] };
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const [groupsRes, tagsRes] = await Promise.all([
        apiClient.get('/api/contacts/groups/list', { headers }).catch(() => ({ data: { groups: [] } })),
        apiClient.get('/api/contacts/tags/list', { headers }).catch(() => ({ data: { tags: [] } }))
      ]);
      return {
        groups: groupsRes.data?.groups || [],
        tags: tagsRes.data?.tags || []
      };
    },
    { enabled: !!currentWorkspace?.id, staleTime: 5 * 60 * 1000 }
  );

  const facebookTemplates = templatesData?.facebookTemplates ?? [];
  const availableGroups = filterData?.groups ?? [];
  const availableTags = filterData?.tags ?? [];

  // Helper function to clean messages - ensure they belong to the chat (defined before useEffect that uses it)
  const cleanChatMessages = useCallback((messages, chatPhoneNormalized) => {
    if (!messages || !Array.isArray(messages)) return [];

    return messages.filter(msg => {
      // Get phone numbers from message
      const fromPhone = normalizePhone(msg.from_number || '');
      const toPhone = normalizePhone(msg.to_number || '');

      // Message belongs to this chat if:
      // 1. from_number matches chat phone (incoming message)
      // 2. to_number matches chat phone (outgoing message)
      // 3. No phone numbers (legacy messages, keep them)
      const belongsToChat =
        fromPhone === chatPhoneNormalized ||
        toPhone === chatPhoneNormalized ||
        (!fromPhone && !toPhone);

      if (!belongsToChat) {
        console.warn('⚠️ Removing message that doesn\'t belong to chat:', {
          messageId: msg.id,
          messageText: msg.message_text?.substring(0, 30),
          fromPhone,
          toPhone,
          chatPhone: chatPhoneNormalized
        });
      }

      return belongsToChat;
    });
  }, []);

  // Keep local state for realtime updates
  const [chats, setChats] = useState([]);
  const activeRecentCount = chatsData?.activeRecentCount ?? 0;

  // Keep selectedChatPhoneRef in sync with selectedChat (for open-from-Contacts fetch guard)
  useEffect(() => {
    selectedChatPhoneRef.current = selectedChat ? normalizePhone(selectedChat.phone) || '' : '';
  }, [selectedChat]);

  // Update local state when React Query data changes — keep locally opened contacts not on current API page
  useEffect(() => {
    if (chatsData === undefined) return;
    const apiChats = (chatsData.chats || []).map(chat => ({
      ...chat,
      messages: cleanChatMessages(chat.messages || [], normalizePhone(chat.phone) || '')
    }));
    setChats(prev => {
      const apiPhoneSet = new Set(apiChats.map(c => normalizePhone(c.phone)).filter(Boolean));

      // No filters: API paginates — replace list, only keep selected chat if not on this page
      if (!hasActiveFilters) {
        const selectedNorm = selectedChatPhoneRef.current;
        if (selectedNorm && !apiPhoneSet.has(selectedNorm)) {
          const opened = prev.find(c => normalizePhone(c.phone) === selectedNorm);
          if (opened) return [opened, ...apiChats];
        }
        return apiChats;
      }

      // Filters active: merge locally opened contacts not returned by API
      const localOnly = prev.filter(c => {
        const p = normalizePhone(c.phone);
        return p && !apiPhoneSet.has(p);
      });
      const merged = [...localOnly];
      apiChats.forEach(chat => {
        const p = normalizePhone(chat.phone);
        if (!merged.some(c => normalizePhone(c.phone) === p)) merged.push(chat);
      });
      return merged;
    });
  }, [chatsData, cleanChatMessages, hasActiveFilters]);

  // Clear selected chat only when it is gone from the local chat list (not just missing from API page 1)
  useEffect(() => {
    if (!selectedChat || loading) return;
    const selectedNorm = normalizePhone(selectedChat.phone);
    if (!selectedNorm) return;
    const stillInList = chats.some(c => normalizePhone(c.phone) === selectedNorm);
    if (!stillInList) {
      setSelectedChat(null);
      setShowMobileChat(false);
    }
  }, [chats, selectedChat, loading]);

  // Simple function to calculate 24-hour window status
  const calculateWindowStatus = (chat) => {
    if (!chat || !chat.messages || chat.messages.length === 0) {
      return {
        isActive: false,
        lastClientMessageAt: null,
        timeRemaining: 0,
        canSendDirect: false,
        canSendFacebookTemplate: true
      };
    }

    // Find the last client message (incoming message)
    const clientMessages = chat.messages.filter(msg =>
      msg.type === 'whatsapp' &&
      msg.from_number &&
      !msg.is_outgoing
    );

    if (clientMessages.length === 0) {
      return {
        isActive: false,
        lastClientMessageAt: null,
        timeRemaining: 0,
        canSendDirect: false,
        canSendFacebookTemplate: true
      };
    }

    // Get the most recent client message
    const lastClientMessage = clientMessages.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    )[0];

    // Simple calculation: last message time + 24 hours
    const lastMessageTime = new Date(lastClientMessage.created_at);
    const now = new Date();

    // Always calculate from the message time + 24 hours
    const expiryTime = new Date(lastMessageTime.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours
    const timeDiff = expiryTime - now;
    const hoursRemaining = Math.max(0, timeDiff / (1000 * 60 * 60));

    // Check if message is in the future (server time issue)
    const isMessageInFuture = lastMessageTime > now;

    if (isMessageInFuture) {
      console.log('⚠️ Message is in the future, but calculating normally');
    }

    // Manual verification calculation
    const manualElapsed = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
    const manualRemaining = Math.max(0, 24 - manualElapsed);

    console.log('🔍 Manual Verification:', {
      manualElapsed: manualElapsed,
      manualRemaining: manualRemaining,
      isMessageInFuture: isMessageInFuture,
      finalHoursRemaining: hoursRemaining,
      status: isMessageInFuture ? 'Future message - 24h window active' : 'Normal calculation'
    });

    // Debug logging with IST conversion
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const lastMessageIST = new Date(lastMessageTime.getTime() + istOffset);
    const nowIST = new Date(now.getTime() + istOffset);

    // Calculate elapsed time since message
    const elapsedTime = now.getTime() - lastMessageTime.getTime();
    const elapsedHours = elapsedTime / (1000 * 60 * 60);
    const expectedRemaining = Math.max(0, 24 - elapsedHours);

    console.log('🕐 24h Window Debug:', {
      rawMessageTime: lastClientMessage.created_at,
      lastMessageUTC: lastMessageTime.toISOString(),
      lastMessageIST: lastMessageIST.toISOString(),
      nowUTC: now.toISOString(),
      nowIST: nowIST.toISOString(),
      elapsedTime: elapsedTime,
      elapsedHours: elapsedHours,
      expectedRemaining: expectedRemaining,
      actualHoursRemaining: hoursRemaining,
      currentYear: now.getFullYear(),
      messageYear: lastMessageTime.getFullYear(),
      isMessageInFuture: isMessageInFuture
    });

    return {
      isActive: hoursRemaining > 0,
      lastClientMessageAt: lastMessageTime.toISOString(),
      canSendDirect: hoursRemaining > 0,
      canSendFacebookTemplate: true
    };
  };


  // Function to refresh current chat messages (with optional silent mode)
  const refreshCurrentChat = useCallback(async (silent = false) => {
    if (!selectedChat) return;

    try {
      if (!silent) {
        setLoadingMessages(true);
      }
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: {
          phoneNumber: selectedChat.phone,
          limit: 100,
          type: 'whatsapp'
        }
      });

      if (response.data?.messages) {
        // Merge with existing messages and deduplicate
        // IMPORTANT: Backend may return messages for multiple contacts.
        // We must filter strictly to the current chat's phone so that
        // other chats' messages (same template content) don't appear here.
        const currentPhoneNormalized = normalizePhone(selectedChat.phone) || '';

        setMessages(prevMessages => {
          const withoutOptimistic = prevMessages.filter(m => !isTempMessageId(m.id));
          const combined = [...withoutOptimistic, ...response.data.messages];
          const allMessages = cleanChatMessages(combined, currentPhoneNormalized);
          const sortedMessages = dedupeMessages(allMessages);

          if (!silent) {
            console.log(`✅ Refreshed messages: ${sortedMessages.length} unique messages`);
          }

          setMessagePages(1);
          setAutoScrollEnabled(true);
          return sortedMessages;
        });
      }
    } catch (error) {
      console.error('Error refreshing chat messages:', error);
      if (!silent) {
        toast.error('Failed to refresh messages');
      }
    } finally {
      if (!silent) {
        setLoadingMessages(false);
      }
    }
  }, [selectedChat, currentWorkspace?.id, cleanChatMessages]);

  const loadMoreOlderMessages = useCallback(async () => {
    if (!selectedChat || !hasMoreOlderMessages || loadingOlderMessages) return;
    setLoadingOlderMessages(true);
    try {
      const nextPage = messageApiPageRef.current + 1;
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: { phoneNumber: selectedChat.phone, page: nextPage, limit: 50, type: 'whatsapp' }
      });
      const older = response.data?.messages || [];
      setHasMoreOlderMessages(older.length >= 50);
      messageApiPageRef.current = nextPage;
      if (older.length > 0) {
        const currentPhoneNorm = normalizePhone(selectedChat.phone) || '';
        const cleaned = cleanChatMessages(older, currentPhoneNorm);
        const reversed = [...cleaned].reverse();
        setMessages(prev => dedupeMessages([...reversed, ...prev]));
        setAutoScrollEnabled(false);
      }
    } catch (err) {
      console.error('Load older messages error:', err);
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [selectedChat, hasMoreOlderMessages, loadingOlderMessages, currentWorkspace?.id, cleanChatMessages]);

  const loadMessagesForPhone = useCallback(async (contactPhone, targetPhoneLast10) => {
    setLoadingMessages(true);
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const res = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: { phoneNumber: contactPhone, limit: 100, type: 'whatsapp' }
      });
      const list = res.data?.messages || [];
      const sorted = dedupeMessages(cleanChatMessages(list, targetPhoneLast10));
      setMessages(sorted);
      setHasMoreOlderMessages(list.length >= 100);
      messageApiPageRef.current = 1;
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [currentWorkspace?.id, cleanChatMessages]);

  const findOrBuildChatForContact = useCallback(async (contactPhone, contactName, chatsList) => {
    const targetPhoneLast10 = normalizePhone(contactPhone) || '';
    const fromList = (chatsList || []).find(c => normalizePhone(c.phone) === targetPhoneLast10);
    if (fromList) return fromList;

    const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
    const searchKey = contactPhone || targetPhoneLast10;
    try {
      const res = await apiClient.get('/api/whatsapp/chats', {
        headers,
        params: { search: searchKey, limit: 20, messagesPerChat: 50 }
      });
      const match = (res.data?.chats || []).find(c => normalizePhone(c.phone) === targetPhoneLast10);
      if (match) {
        return { ...match, messages: [...(match.messages || [])].reverse() };
      }
    } catch (err) {
      console.warn('Chat lookup by phone failed:', err.message);
    }

    return {
      id: contactPhone,
      name: contactName || contactPhone,
      phone: contactPhone,
      phoneNumber: contactPhone,
      normalizedPhone: targetPhoneLast10,
      messages: [],
      lastMessage: 'No messages yet',
      timestamp: 'New conversation',
      unread: 0,
      unreadCount: 0,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName || contactPhone)}&background=random`,
      group: '',
      tags: []
    };
  }, [currentWorkspace?.id]);

  // Function to select a chat
  const handleChatSelect = async (chat) => {
    if (!chat?.phone) return;
    const newPhoneNorm = normalizePhone(chat.phone) || '';
    const isSameChat = selectedChatPhoneRef.current === newPhoneNorm;

    selectedChatPhoneRef.current = newPhoneNorm;
    setSelectedChat(chat);
    // On mobile, show chat view and hide list
    setShowMobileChat(true);

    // Remove duplicates before displaying
    const sortedMessages = dedupeMessages(cleanChatMessages(chat.messages || [], newPhoneNorm));
    if (isSameChat) {
      setMessages(prev => dedupeMessages([...prev, ...sortedMessages]));
    } else {
      setMessages(sortedMessages);
    }
    setMessagePages(1);
    setAutoScrollEnabled(true);
    messageApiPageRef.current = 1;
    setHasMoreOlderMessages((chat.messages?.length || 0) >= 50);

    // Calculate 24-hour window status for this chat
    const newWindowStatus = calculateWindowStatus(chat);
    setWindowStatus(newWindowStatus);

    // Mark messages as read when chat is selected
    if (chat.unread > 0) {
      try {
        // Update unread count in local state immediately for better UX
        setChats(prevChats =>
          prevChats.map(c =>
            c.id === chat.id ? { ...c, unread: 0 } : c
          )
        );

        // Also update filtered chats if they exist
        setFilteredChats(prevFiltered =>
          prevFiltered.map(c =>
            c.id === chat.id ? { ...c, unread: 0 } : c
          )
        );

        // Mark messages as read in the database
        await apiClient.post('/api/whatsapp/messages/mark-read', {
          phoneNumber: chat.phone
        }, {
          headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {}
        });

        console.log('✅ Marked messages as read for:', chat.phone);
      } catch (error) {
        console.error('Error marking messages as read:', error);
        // If the API call fails, we still want to keep the local state updated
        // The unread count will be corrected on the next reload
      }
    }

    // Force scroll to bottom when chat is selected
    setTimeout(() => {
      if (messagesContainerRef.current) {
        // Scroll the container to bottom
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      } else if (messagesEndRef.current) {
        // Fallback to scrollIntoView
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };
  handleChatSelectRef.current = handleChatSelect;

  // Auto-select chat when navigated from Contacts, analytics, or notifications
  useEffect(() => {
    const contactPhone = openContactFromState || contactQueryParam;
    const contactName = contactNameFromState || contactNameParam || '';

    if (!contactPhone || loading || !chatsData || !currentWorkspace?.id) return;

    const targetPhoneLast10 = normalizePhone(contactPhone) || '';
    const openKey = `${currentWorkspace.id}:${targetPhoneLast10}:${contactOpenToken || openContactFromState || ''}`;

    if (processedContactRef.current === openKey) return;

    let cancelled = false;
    const openKeyForRun = openKey;

    (async () => {
      try {
        const chatsList = chatsData.chats || [];
        const chatToOpen = await findOrBuildChatForContact(contactPhone, contactName, chatsList);
        if (cancelled) return;

        setChats(prev => {
          const p = normalizePhone(chatToOpen.phone);
          if (prev.some(c => normalizePhone(c.phone) === p)) return prev;
          return [chatToOpen, ...prev];
        });

        handleChatSelectRef.current?.(chatToOpen);
        await loadMessagesForPhone(contactPhone, targetPhoneLast10);

        if (cancelled) return;
        processedContactRef.current = openKeyForRun;

        if (contactQueryParam || contactNameParam || contactOpenToken) {
          setSearchParams({}, { replace: true });
        } else if (openContactFromState) {
          navigate('/chats', { replace: true, state: {} });
        }
      } catch (err) {
        console.error('Failed to open contact chat:', err);
        if (processedContactRef.current === openKeyForRun) {
          processedContactRef.current = null;
        }
        if (!cancelled) toast.error('Failed to open chat');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    openContactFromState,
    contactQueryParam,
    contactNameFromState,
    contactNameParam,
    contactOpenToken,
    loading,
    currentWorkspace?.id,
    findOrBuildChatForContact,
    loadMessagesForPhone,
    setSearchParams,
    navigate,
    chatsData
  ]);

  // Optimistic realtime update on new message (insert into UI without reload)
  useEffect(() => {
    console.log('🎯 Setting up SSE listener (useEffect triggered)');

    const onNewMessage = (e) => {
      const data = e.detail;
      console.log('🔔 Real-time message received:', data);

      if (data?.workspaceId && currentWorkspace?.id && data.workspaceId !== currentWorkspace.id) {
        console.log('❌ Wrong workspace, ignoring');
        return;
      }

      const phone = data?.fromNumber || data?.toNumber;
      if (!phone) {
        console.log('❌ No phone number in message');
        return;
      }

      // Use the actual created_at from database, fallback to current time
      const messageTimestamp = data.createdAt || new Date().toISOString();
      console.log('⏰ Message timestamp:', messageTimestamp, 'from server:', !!data.createdAt);

      // Look up contact information
      const contact = findContact(phone);
      console.log('🔍 Contact lookup result:', { phone, contact, hasContact: !!contact });

      const contactName = contact ? contact.name : phone;
      const contactGroup = contact ? contact.group_name : '';
      const contactTags = contact ? contact.tags : [];

      // Contact details processed

      const incoming = {
        id: data.dbMessageId || data.messageId || `tmp-${Date.now()}`,
        type: 'whatsapp',
        from_number: data.fromNumber || null,
        to_number: data.toNumber || null,
        message_text: data.messageText || '',
        status: '',
        media_url: data.mediaUrl || null,
        media_type: data.mediaType || null,
        created_at: messageTimestamp,
        contact_name: contactName,
        is_outgoing: data.fromNumber && !data.toNumber ? false : Boolean(data.toNumber && !data.fromNumber),
      };

      // Normalize phone numbers for comparison (last 10 digits)
      const incomingPhoneLast10 = normalizePhone(phone) || '';

      console.log('📞 Incoming phone (last 10):', incomingPhoneLast10);
      console.log('📱 Selected chat phone (last 10):', selectedChatPhoneRef.current || 'none');

      // If we've just sent a message from this chat and are doing a manual refresh,
      // temporarily ignore SSE events for this phone to avoid duplicate inserts
      if (sseIgnorePhoneRef.current && sseIgnorePhoneRef.current === incomingPhoneLast10) {
        console.log('⏭️ Ignoring SSE message for phone due to recent manual refresh:', incomingPhoneLast10);
        return;
      }

      setChats(prev => {
        const exists = prev.find(c => normalizePhone(c.phone) === incomingPhoneLast10);
        let updatedChats;

        if (exists) {
          // Update existing chat and move it to the top
          // CRITICAL: Never replace a proper name with a phone number
          let finalName = exists.name;
          let finalGroup = exists.group;
          let finalTags = exists.tags;
          let finalAvatar = exists.avatar;

          if (contact) {
            // We have valid contact info - update if the name is not just a phone number
            if (contact.name && contact.name.trim() && contact.name.trim() !== phone) {
              finalName = contact.name.trim();
              // Only regenerate avatar if we have a proper name
              finalAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name.trim())}&background=random`;
            }
            if (contactGroup) {
              finalGroup = contactGroup;
            }
            if (contactTags && contactTags.length > 0) {
              finalTags = contactTags;
            }
          }
          // else: No contact found - keep ALL existing data (name, avatar, group, tags)

          console.log('📝 Chat update decision:', {
            existingName: exists.name,
            newContactName: contact?.name,
            finalNameUsed: finalName,
            avatarUpdated: finalAvatar !== exists.avatar,
            reason: contact ? 'Contact found' : 'No contact - keeping existing'
          });

          // CRITICAL: Verify that incoming message belongs to this chat
          // Filter out any existing messages that don't belong to this chat
          const existingMessages = dedupeMessages((exists.messages || []).filter(msg => {
            const msgPhone = normalizePhone(msg.from_number || msg.to_number || '');
            return msgPhone === incomingPhoneLast10 || !msgPhone;
          }));

          // Ensure incoming message belongs to this chat
          const incomingMsgPhone = normalizePhone(incoming.from_number || incoming.to_number || '');
          if (incomingMsgPhone && incomingMsgPhone !== incomingPhoneLast10) {
            console.warn('⚠️ Message phone mismatch - skipping merge:', {
              incomingPhone: incomingMsgPhone,
              chatPhone: incomingPhoneLast10
            });
            return prev;
          }

          const mergedMessages = dedupeMessages([incoming, ...existingMessages]);

          const updatedChat = {
            ...exists,
            name: finalName,
            avatar: finalAvatar,
            group: finalGroup,
            tags: finalTags,
            messages: mergedMessages,
            lastMessage: incoming.message_text || 'Media message',
            timestamp: 'just now',
            unread: (selectedChatPhoneRef.current === incomingPhoneLast10) ? 0 : ((exists.unread || 0) + 1)
          };

          // Remove the existing chat and add it to the top
          const otherChats = prev.filter(c => normalizePhone(c.phone) !== incomingPhoneLast10);
          updatedChats = [updatedChat, ...otherChats];

          // Note: We don't update selectedChat here to avoid triggering re-renders
          // The selectedChat will be synced when the user interacts with it
        } else {
          // Create new chat at the top
          const newChat = {
            id: phone,
            name: contactName,
            phone,
            group: contactGroup,
            tags: contactTags,
            messages: [incoming],
            lastMessage: incoming.message_text || 'Media message',
            timestamp: 'just now',
            unread: (selectedChatPhoneRef.current === incomingPhoneLast10) ? 0 : 1,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`
          };
          updatedChats = [newChat, ...prev];
        }

        // Don't call updateFilteredChats here - let the useEffect handle it
        // This prevents infinite loops when chats state changes

        return updatedChats;
      });

      // Silently refresh chat list after a short delay to get the latest data from DB
      // This ensures we have the correct message IDs, delivery status, and all metadata
      // Silent refresh means no loading spinners, no toasts - just smooth real-time updates
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ['chats', currentWorkspace?.id],
          type: 'active'
        }, {
          // Silent refetch - no loading states, no error toasts
          throwOnError: false
        }).catch(err => {
          // Silently handle errors - don't show toasts for background refreshes
          console.error('Silent chat list refresh error:', err);
        });
      }, 1000);

      // Update messages in chat area if this chat is currently selected
      // Use normalized phone comparison to handle different formats
      if (selectedChatPhoneRef.current === incomingPhoneLast10) {
        console.log('✅ Updating messages in chat area for selected chat');
        setMessages(prevMsgs => {
          if (prevMsgs.some(m => areMessagesDuplicate(m, incoming))) {
            console.log('⚠️ Duplicate message detected, skipping SSE insert');
            return prevMsgs;
          }
          const merged = dedupeMessages([...prevMsgs, incoming]);
          console.log('📝 Adding new message:', incoming.message_text, 'at', incoming.created_at);
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          }, 100);
          return merged;
        });

        setTimeout(() => {
          refreshCurrentChat(true);
        }, 1000);

        if (incoming.from_number && !incoming.is_outgoing) {
          setWindowStatus({
            isActive: true,
            lastClientMessageAt: incoming.created_at,
            canSendDirect: true,
            canSendFacebookTemplate: true
          });
        }
      } else {
        console.log('ℹ️ Chat not selected or phone mismatch');
      }
    };

    window.addEventListener('sse:new-message', onNewMessage);
    console.log('✅ SSE listener attached');

    return () => {
      window.removeEventListener('sse:new-message', onNewMessage);
      console.log('🔴 SSE listener removed (cleanup)');
    };
  }, [currentWorkspace?.id, refreshCurrentChat, queryClient, cleanChatMessages]);

  // Update filtered chats with given chat list
  const updateFilteredChats = useCallback((chatList) => {
    let filtered = [...chatList];

    // Filter by search term (name or phone)
    if (searchTerm) {
      filtered = filtered.filter(chat => {
        const name = chat.name || '';
        const phone = chat.phone || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm);
      });
    }

    // Filter by group (if contact has group info)
    if (selectedGroup) {
      const gf = selectedGroup.trim().toLowerCase();
      filtered = filtered.filter(chat => {
        const g = (chat.group || '').trim().toLowerCase();
        if (gf === 'no group') {
          return !g || g === 'from messages';
        }
        return g === gf;
      });
    }

    // Filter by tag (if contact has tag info)
    if (selectedTag) {
      const tf = selectedTag.trim().toLowerCase();
      filtered = filtered.filter(chat => {
        if (tf === 'no tags') {
          return !Array.isArray(chat.tags) || chat.tags.length === 0;
        }
        return Array.isArray(chat.tags) &&
          chat.tags.some(t => String(t || '').toLowerCase() === tf);
      });
    }

    // Active recent is filtered server-side across all contacts

    // Sort by most recent message first (independent of filters - always recalc for SSE/optimistic updates)
    filtered.sort((a, b) => {
      const ta = getChatLatestTimestamp(a);
      const tb = getChatLatestTimestamp(b);
      return tb - ta || 0; // stable: most recent first
    });

    setFilteredChats(filtered);
  }, [searchTerm, selectedGroup, selectedTag]);

  // Filter chats based on search term, group, and tag
  const filterChats = useCallback(() => {
    updateFilteredChats(chats);
  }, [chats, updateFilteredChats]);

  // Filter chats when search term, group, tag, or unread filter changes
  useEffect(() => {
    if (chats.length > 0) {
      filterChats();
    } else {
      setFilteredChats([]);
    }
  }, [filterChats, chats.length]);

  // Reset client-side page when filters change (not on API page change)
  useEffect(() => {
    setCurrentPage(1);
    setChatPage(1);
  }, [searchTerm, selectedGroup, selectedTag, showActiveRecentOnly]);

  // Get chats to render in the sidebar list
  const getCurrentPageChats = () => {
    if (!hasActiveFilters) {
      // API already returns one page — show all loaded chats
      return filteredChats;
    }
    const startIndex = (currentPage - 1) * chatsPerPage;
    const endIndex = startIndex + chatsPerPage;
    return filteredChats.slice(startIndex, endIndex);
  };

  // Get total pages (client-side only when filters are active)
  const getTotalPages = () => {
    if (!hasActiveFilters) {
      return chatsData?.pagination?.pages || 1;
    }
    return Math.ceil(filteredChats.length / chatsPerPage);
  };

  // Auto-scroll to bottom when visible messages change (new sends / refresh)
  useEffect(() => {
    if (!autoScrollEnabled) return;

    // Use setTimeout to ensure DOM is updated before scrolling
    const scrollToBottom = () => {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          // Scroll the container to bottom
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        } else if (messagesEndRef.current) {
          // Fallback to scrollIntoView
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    };

    scrollToBottom();
  }, [visibleMessages, autoScrollEnabled]);

  // Whenever we change chats, reset to show only the latest page of messages
  useEffect(() => {
    setMessagePages(1);
    setAutoScrollEnabled(true);
  }, [selectedChat?.phone]);

  // Sync selectedChat metadata from chat list (avoid selectedChat in deps to prevent update loops)
  useEffect(() => {
    const phone = selectedChatPhoneRef.current;
    if (!phone || chats.length === 0) return;

    const updatedChat = chats.find(c => normalizePhone(c.phone) === phone);
    if (!updatedChat) return;

    setSelectedChat(prev => {
      if (!prev || normalizePhone(prev.phone) !== phone) return prev;
      if (
        prev.lastMessage === updatedChat.lastMessage &&
        prev.unread === updatedChat.unread &&
        prev.name === updatedChat.name &&
        prev.avatar === updatedChat.avatar
      ) {
        return prev;
      }
      return {
        ...updatedChat,
        messages: prev.messages || updatedChat.messages
      };
    });
  }, [chats]);

  // Contact lookup for SSE new messages - returns null (contact info comes from messages API on next refresh)
  const findContact = () => null;

  // Pagination helpers for chat list (sidebar, filters only)
  const goToChatPage = (page) => {
    const totalPages = getTotalPages();
    const safePage = Math.min(Math.max(1, page), totalPages || 1);
    setCurrentPage(safePage);
  };

  const nextChatPage = () => {
    goToChatPage(currentPage + 1);
  };

  const prevChatPage = () => {
    goToChatPage(currentPage - 1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedGroup('');
    setSelectedTag('');
    setShowActiveRecentOnly(false);
    setCurrentPage(1);
    setChatPage(1);
  };

  // DEPRECATED: fetchTemplates removed - templates are now loaded via React Query (useQuery hook)
  // Templates are automatically loaded in the 'chats' query (lines 170-411)

  // DEPRECATED: loadMessages_REMOVED function removed - now using React Query for data loading
  // All message loading logic is handled in the useQuery hook above (lines 170-411)

  const handleSendMessage = async () => {
    if (!selectedChat || (!message.trim() && !selectedTemplate && !selectedMedia)) return;

    // Check 24-hour window for media messages
    if (selectedMedia && !windowStatus.canSendDirect) {
      toast.error('Cannot send media: 24-hour window expired. Only Facebook templates are allowed.');
      return;
    }

    const selectedChatPhoneNormalized = normalizePhone(selectedChat.phone);
    let messageContent = '';
    let tempId = `tmp-${Date.now()}`;

    const addOptimisticAndReset = (content) => {
      if (!content) return;
      const optimisticMsg = {
        id: tempId,
        message_text: content,
        to_number: selectedChat.phone,
        from_number: null,
        created_at: new Date().toISOString(),
        status: 'sent',
        delivery_status: 'sent',
        is_outgoing: true
      };
      setMessages(prev => dedupeMessages([...prev, optimisticMsg]));
      setMessagePages(1);
      setAutoScrollEnabled(true);
      setMessage('');
      setSelectedTemplate(null);
      setTemplateParams({});
      setUseTemplate(false);
      setShowTemplateDropdown(false);
      setSelectedMedia(null);
      setMediaPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const now = new Date().toISOString();
      const displayForList = content || 'Media message';
      const patch = { unread: 0, lastMessage: displayForList, timestamp: 'just now', lastMessageObj: { created_at: now } };
      setChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
      setFilteredChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
    };

    const addOptimisticMediaAndReset = (file, caption) => {
      const blobUrl = URL.createObjectURL(file);
      blobUrlsToRevokeRef.current.push(blobUrl);
      const mime = (file.type || '').toLowerCase();
      let mediaType = 'document';
      if (mime.startsWith('image/')) mediaType = 'image';
      else if (mime.startsWith('video/')) mediaType = 'video';
      else if (mime.startsWith('audio/')) mediaType = 'audio';
      const optimisticMsg = {
        id: tempId,
        message_text: caption || '',
        media_url: blobUrl,
        media_type: mediaType,
        to_number: selectedChat.phone,
        from_number: null,
        created_at: new Date().toISOString(),
        status: 'sent',
        delivery_status: 'sent',
        is_outgoing: true
      };
      setMessages(prev => dedupeMessages([...prev, optimisticMsg]));
      setMessagePages(1);
      setAutoScrollEnabled(true);
      setMessage('');
      setSelectedTemplate(null);
      setTemplateParams({});
      setUseTemplate(false);
      setShowTemplateDropdown(false);
      setSelectedMedia(null);
      setMediaPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const displayForList = caption?.trim() || 'Media message';
      const now = new Date().toISOString();
      const patch = { unread: 0, lastMessage: displayForList, timestamp: 'just now', lastMessageObj: { created_at: now } };
      setChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
      setFilteredChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
    };

    try {

      if (useTemplate && selectedTemplate) {
        // Send Facebook/WhatsApp template via send-message
        const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

        const bodyComponent = selectedTemplate.components?.find(c => c.type === 'BODY');
        const placeholderOrder = [];
        if (bodyComponent?.text) {
          const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || [];
          matches.forEach((m) => {
            const idx = Number(m.replace(/[^0-9]/g, ''));
            if (!Number.isNaN(idx)) placeholderOrder.push(idx);
          });
        }

        const templateParameters = placeholderOrder.map((n) => {
          const value = templateParams[`param${n}`] || '';
          return { type: 'text', text: value };
        });

        if (placeholderOrder.length > 0 && templateParameters.some(p => !p.text || !`${p.text}`.trim())) {
          toast.error('Please fill all required template parameters.');
          return;
        }

        messageContent = bodyComponent?.text || `[Template: ${selectedTemplate.name}]`;
        placeholderOrder.forEach((n) => {
          messageContent = messageContent.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), templateParams[`param${n}`] || '');
        });
        setSendInProgress(true);
        addOptimisticAndReset(messageContent);
        const payload = {
          phoneNumber: selectedChat.phone,
          templateName: selectedTemplate.name,
          templateLanguage: selectedTemplate.language || 'en_US',
          messageText: messageContent
        };
        if (templateParameters.length > 0) payload.templateParameters = templateParameters;
        const sendResponse = await apiClient.post('/api/whatsapp/send-message', payload, { headers });
        if (sendResponse.data?.dbMessageId) {
          setMessages(prev => dedupeMessages(prev.map(m => (
            m.id === tempId ? { ...m, id: sendResponse.data.dbMessageId } : m
          ))));
        }
      } else {
        // Send regular message (with or without media)
        let mediaUrl = null;
        let mediaType = null;
        let whatsappType = null;
        let apiMessageText = message.trim();

        if (selectedMedia) {
          // Optimistic: show media instantly (like text), then upload + send in background
          const mediaCaption = message.trim();
          apiMessageText = mediaCaption;
          setSendInProgress(true);
          addOptimisticMediaAndReset(selectedMedia, mediaCaption);
          try {
            const formData = new FormData();
            formData.append('media', selectedMedia);
            const uploadResponse = await apiClient.post('/api/whatsapp/upload-media', formData, {
              headers: { 'x-workspace-id': currentWorkspace?.id || '', 'Content-Type': 'multipart/form-data' }
            });
            mediaUrl = uploadResponse.data.filePath;
            mediaType = uploadResponse.data.mediaType;
            whatsappType = uploadResponse.data.whatsappType;
          } catch (uploadError) {
            toast.error('Failed to upload media. Please try again.');
            setMessages(prev => prev.filter(m => m.id !== tempId));
            blobUrlsToRevokeRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) { } });
            blobUrlsToRevokeRef.current = [];
            setSendInProgress(false);
            return;
          }
        } else {
          // Text message - optimistic add instantly (WhatsApp-like)
          messageContent = message.trim();
          apiMessageText = messageContent;
          setSendInProgress(true);
          addOptimisticAndReset(messageContent);
        }

        const sendResponse = await apiClient.post('/api/whatsapp/send-message', {
          phoneNumber: selectedChat.phone,
          messageText: apiMessageText,
          mediaUrl: mediaUrl,
          mediaType: mediaType,
          whatsappType: whatsappType
        }, { headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {} });
        if (sendResponse.data?.dbMessageId) {
          setMessages(prev => dedupeMessages(prev.map(m => (
            m.id === tempId ? { ...m, id: sendResponse.data.dbMessageId } : m
          ))));
        }
      }

      // After sending from this chat, temporarily ignore SSE messages for this phone
      // while we perform a manual refresh from the server. This prevents the same
      // message from being added once via SSE and again via the API refresh.
      if (selectedChatPhoneNormalized) {
        sseIgnorePhoneRef.current = selectedChatPhoneNormalized;
        if (sseIgnoreTimeoutRef.current) {
          clearTimeout(sseIgnoreTimeoutRef.current);
        }
        sseIgnoreTimeoutRef.current = setTimeout(() => {
          sseIgnorePhoneRef.current = null;
          sseIgnoreTimeoutRef.current = null;
        }, 5000); // 5 seconds is enough for refresh + server processing
      }

      // Wait a bit for the server to process and then refresh messages
      // Don't invalidate queries immediately to avoid duplicate messages from SSE + refetch
      // Use a longer delay for templates to ensure server has processed the message
      const refreshDelay = useTemplate ? 2500 : 2000;
      setTimeout(async () => {
        try {
          // Refresh current chat messages to get latest from server (with deduplication)
          // This will merge with existing messages and remove duplicates
          await refreshCurrentChat(true); // Silent refresh to avoid loading flicker

          // Then invalidate and refetch the chats query to update the chat list
          // This ensures we get the latest chat order and metadata from server
          await queryClient.refetchQueries({
            queryKey: ['chats', currentWorkspace?.id],
            type: 'active'
          });
        } catch (error) {
          console.error('Error refreshing chats after send:', error);
          queryClient.invalidateQueries(['chats', currentWorkspace?.id]);
        } finally {
          setLoadingMessages(false);
          setAutoScrollEnabled(true);
          blobUrlsToRevokeRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) { } });
          blobUrlsToRevokeRef.current = [];
        }
      }, refreshDelay);
    } catch (error) {
      console.error('Send message error:', error.response?.data);
      setMessages(prev => prev.filter(m => m.id !== tempId));
      blobUrlsToRevokeRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) { } });
      blobUrlsToRevokeRef.current = [];
      const errorMessage = error.response?.data?.error || error.response?.data?.details?.error?.message || 'Failed to send message';
      toast.error(`Send failed: ${errorMessage}`);
    } finally {
      setSending(false);
      setSendInProgress(false);
    }
  };

  // Helper function to format date headers (Today, Yesterday, or date - like WhatsApp)
  const formatDateHeader = (date) => {
    return formatLocalTime(date, 'date-header');
  };


  // Helper function to group messages by date (using user's local timezone - like WhatsApp)
  const groupMessagesByDate = (messages) => {
    const groups = {};

    messages.forEach(message => {
      // Use LOCAL date for grouping (user's timezone), not UTC
      const date = new Date(message.created_at);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: new Date(year, parseInt(month, 10) - 1, parseInt(day, 10)),
          dateKey: dateKey,
          dateHeader: formatDateHeader(message.created_at),
          messages: []
        };
      }

      groups[dateKey].messages.push(message);
    });

    // Convert to array and sort by date (oldest date first)
    const sortedGroups = Object.values(groups).sort((a, b) => a.date - b.date);

    // Sort messages within each group by timestamp (oldest first = ascending, like WhatsApp)
    sortedGroups.forEach(group => {
      group.messages.sort((a, b) => {
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        return timeA - timeB;
      });
    });

    return sortedGroups;
  };


  // Template handling functions
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setUseTemplate(true);
    setShowTemplateDropdown(false);

    // Initialize template parameters
    const params = {};
    if (template.parameters && Array.isArray(template.parameters)) {
      template.parameters.forEach(param => {
        params[param] = '';
      });
    } else if (template.components) {
      const bodyComponent = template.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const paramMatches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
        if (paramMatches) {
          paramMatches.forEach((match) => {
            const paramNumber = match.replace(/[^0-9]/g, '');
            params[`param${paramNumber}`] = '';
          });
        }
      }
    }
    setTemplateParams(params);
  };

  const renderTemplatePreview = () => {
    if (!selectedTemplate) return null;

    let content = selectedTemplate.name;

    const bodyComponent = selectedTemplate.components?.find(c => c.type === 'BODY');
    if (bodyComponent?.text) {
      content = bodyComponent.text;
    }

    Object.keys(templateParams).forEach((param) => {
      const paramNumber = param.replace(/^param/, '');
      const placeholder = `{{${paramNumber}}}`;
      content = content.replace(new RegExp(placeholder, 'g'), templateParams[param] || `[${param}]`);
    });

    return (
      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-blue-800">
            Template Preview:
          </span>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setUseTemplate(false);
              setTemplateParams({});
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-blue-700">{content}</p>
        <p className="text-xs text-blue-600 mt-1">
          Template: {selectedTemplate.name} | Status: {selectedTemplate.status || 'Unknown'}
        </p>
      </div>
    );
  };

  const chatsShellClass =
    'flex flex-col md:flex-row w-full pt-4 min-w-0 max-w-full overflow-hidden bg-gray-50 ' +
    'max-md:rounded-xl max-md:border max-md:border-gray-200 ' +
    'h-[calc(100dvh-4.5rem)] -mt-4 -mb-4 min-h-[420px] ' +
    'sm:-mt-6 sm:-mb-6 ' +
    'md:h-[calc(100vh-4.5rem)] md:-mx-6 md:-mt-6 md:-mb-6 md:w-[calc(100%+3rem)] md:rounded-none md:border-0 ' +
    'lg:-mx-8 lg:-mt-8 lg:-mb-8 lg:w-[calc(100%+4rem)]';

  if (loading) {
    return (
      <div className={`${chatsShellClass} overflow-hidden`}>
        <ChatSkeleton />
      </div>
    );
  }

  return (
    <div className={chatsShellClass}>
      {/* Chat List Sidebar - Hidden on mobile when chat is selected, always visible on desktop */}
      <div className={`${showMobileChat ? 'hidden' : 'flex'} md:flex w-full min-w-0 md:w-1/3 h-full min-h-0 bg-white border-r border-gray-200 flex-col`}>
        {/* Header */}
        <div className="shrink-0 px-3 py-3 sm:px-4 sm:py-4 border-b border-gray-200 bg-white">
          {/* Search */}
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-sm"
            />
          </div>

          {/* Count, filters & pagination */}
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-gray-500 font-medium shrink-0">
              {hasActiveFilters
                ? `${getCurrentPageChats().length} of ${filteredChats.length}`
                : `${filteredChats.length} of ${chatsData?.pagination?.total ?? filteredChats.length}`}
              {chatsData?.pagination?.total > 0 && !hasActiveFilters && (
                <span className="hidden sm:inline">{` (${chatsData.pagination.total} total)`}</span>
              )}
            </span>

            <div className="flex items-center gap-1 flex-wrap justify-end ml-auto">
              <button
                type="button"
                onClick={() => setShowActiveRecentOnly(prev => !prev)}
                title="Last 48h: at least 1 message from you and 1 reply from the contact"
                className={`px-2 py-1 text-xs rounded border transition-colors whitespace-nowrap ${showActiveRecentOnly
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                  }`}
              >
                {showActiveRecentOnly
                  ? `Recent${activeRecentCount ? ` (${activeRecentCount})` : ''}`
                  : 'Recent'}
              </button>

              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-0.5 px-2 py-1 text-xs rounded border transition-colors ${showFilters
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200'
                  }`}
              >
                <ChevronDown className={`h-3 w-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                Filter
              </button>

              {(searchTerm || selectedGroup || selectedTag || showActiveRecentOnly) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-2 py-1 text-xs rounded border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                  title="Clear filters"
                >
                  <X className="h-3 w-3" />
                </button>
              )}

              {chatsData?.pagination && chatsData.pagination.pages > 1 && !hasActiveFilters && (
                <>
                  <button
                    type="button"
                    onClick={() => setChatPage(p => Math.max(1, p - 1))}
                    disabled={chatPage <= 1 || loading}
                    className="px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    Page {chatPage} of {chatsData.pagination.pages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setChatPage(p => Math.min(chatsData.pagination.pages, p + 1))}
                    disabled={chatPage >= chatsData.pagination.pages || loading}
                    className="px-2 py-1 text-xs font-medium border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Filter Dropdowns */}
          {showFilters && (
            <div className="mt-3 grid grid-cols-1 gap-3 p-4 bg-gray-50 rounded-xl">
              {/* Group Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Groups</option>
                  {availableGroups.map((group, index) => (
                    <option key={typeof group === 'string' ? group : `group-${index}`} value={typeof group === 'string' ? group : group.name || group}>
                      {typeof group === 'string' ? group : group.name || group}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Tags</option>
                  {availableTags.map((tag, index) => (
                    <option key={typeof tag === 'string' ? tag : `tag-${index}`} value={typeof tag === 'string' ? tag : tag.name || tag}>
                      {typeof tag === 'string' ? tag : tag.name || tag}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Chat List */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {getCurrentPageChats().map((chat) => (
            <div
              key={normalizePhone(chat.phone) || chat.id}
              onClick={() => handleChatSelect(chat)}
              className={`px-3 py-3 sm:px-4 sm:py-4 cursor-pointer transition-all duration-200  border-gray-200 border-l-4 ${selectedChat?.id === chat.id
                ? 'bg-blue-50 border-l-blue-500 shadow-sm'
                : 'hover:bg-gray-50 border-l-transparent'
                }`}
            >
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={chat.avatar}
                    alt={chat.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  {chat.unread > 0 && (
                    <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {chat.unread > 99 ? '99+' : chat.unread}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {chat.name}
                      </h3>
                      {chat.group && (
                        <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 truncate max-w-[7rem]">
                          {chat.group}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 font-medium shrink-0">{chat.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate leading-relaxed">{chat.lastMessage}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Chat list pagination (client-side when filters active) */}
          {hasActiveFilters && getTotalPages() > 1 && (
            <div className="px-3 py-3 sm:px-4 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
              <button
                onClick={prevChatPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-lg border ${currentPage === 1
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-300 hover:bg-gray-100'
                  }`}
              >
                Previous
              </button>

              <span className="text-[11px]">
                Page {currentPage} of {getTotalPages()} &middot;{' '}
                {filteredChats.length} chats
              </span>

              <button
                onClick={nextChatPage}
                disabled={currentPage === getTotalPages()}
                className={`px-3 py-1 rounded-lg border ${currentPage === getTotalPages()
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-300 hover:bg-gray-100'
                  }`}
              >
                Next
              </button>
            </div>
          )}

          {/* No chats message */}
          {getCurrentPageChats().length === 0 && !loading && (
            <div className="p-8 text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No chats found</p>
              {(searchTerm || selectedGroup || selectedTag || showActiveRecentOnly) && (
                <p className="text-sm text-gray-500 mt-2">Try adjusting your filters</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages Area - Hidden on mobile when no chat selected, always visible on desktop */}
      <div className={`${!showMobileChat && !selectedChat ? 'hidden' : 'flex'} md:flex flex-1 w-full min-w-0 md:w-2/3 h-full min-h-0 flex-col bg-white`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="shrink-0 px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                {/* Back button for mobile */}
                <button
                  onClick={() => {
                    setShowMobileChat(false);
                    setSelectedChat(null);
                  }}
                  className="md:hidden mr-3 p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Back to chat list"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <div className="flex items-center space-x-3 flex-1">
                  <img
                    src={selectedChat.avatar}
                    alt={selectedChat.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-medium text-gray-900">{selectedChat.name}</h3>
                      {selectedChat.group && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                          {selectedChat.group}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{selectedChat.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 sm:p-6 bg-gray-50">
              {loadingMessages ? (
                <ChatMessagesSkeleton />
              ) : (
                <div className="space-y-6">
                  {/* Load older messages: from memory or fetch from API */}
                  {(messages.length > visibleMessages.length || hasMoreOlderMessages) && (
                    <div className="flex justify-center">
                      <button
                        onClick={async () => {
                          if (messages.length > visibleMessages.length) {
                            setAutoScrollEnabled(false);
                            setMessagePages(prev => Math.min(prev + 1, Math.ceil(messages.length / MESSAGE_PAGE_SIZE)));
                          } else if (hasMoreOlderMessages) {
                            await loadMoreOlderMessages();
                          }
                        }}
                        disabled={loadingOlderMessages}
                        className="px-4 py-1 text-xs rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        {loadingOlderMessages ? 'Loading...' : 'Load older messages'}
                      </button>
                    </div>
                  )}

                  {visibleMessages.length > 0 ? (
                    (() => {
                      // Group messages by date (will be sorted within groups)
                      const groupedMessages = groupMessagesByDate(visibleMessages);

                      return groupedMessages.map((group, groupIndex) => (
                        <div key={groupIndex} className="space-y-3">
                          {/* Date Header */}
                          <div className="flex justify-center">
                            <div className="bg-white border border-gray-200 text-gray-600 text-xs px-4 py-2 rounded-full shadow-sm">
                              {group.dateHeader}
                            </div>
                          </div>

                          {/* Messages for this date */}
                          {group.messages.map((msg, index) => {
                            // Determine if message is outgoing (sent by us) or incoming (received from customer)
                            const isOutgoing = msg.type === 'outgoing' ||
                              (msg.to_number && !msg.from_number) ||
                              msg.status === 'sent';

                            // Get delivery status for tick marks (sent, delivered, read)
                            const deliveryStatus = msg.delivery_status || (msg.status === 'sent' ? 'sent' : null);

                            // Determine tick mark color and icon
                            let tickIcon = null;
                            let tickColor = '';
                            if (isOutgoing && deliveryStatus) {
                              if (deliveryStatus === 'read') {
                                tickIcon = <CheckCheck style={{ width: '1.275rem', height: '1.275rem' }} />;
                                tickColor = 'text-blue-600'; // Dark blue for read
                              } else if (deliveryStatus === 'delivered') {
                                tickIcon = <CheckCheck style={{ width: '1.275rem', height: '1.275rem' }} />;
                                tickColor = 'text-blue-400'; // Light blue for delivered
                              } else if (deliveryStatus === 'sent') {
                                tickIcon = <Check style={{ width: '1.275rem', height: '1.275rem' }} />;
                                tickColor = 'text-blue-400'; // Light blue for sent
                              }
                            }

                            // Get API base URL for media files (blob URLs for optimistic display as-is)
                            const mediaUrl = msg.media_url
                              ? (String(msg.media_url).startsWith('blob:') ? msg.media_url : `${getApiUrl()}${msg.media_url}`)
                              : null;
                            // Detect media type from URL if not provided
                            let mediaType = msg.media_type;
                            if (!mediaType && mediaUrl) {
                              const urlLower = mediaUrl.toLowerCase();
                              if (urlLower.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
                                mediaType = 'image';
                              } else if (urlLower.match(/\.(mp4|3gp|webm)$/)) {
                                mediaType = 'video';
                              } else if (urlLower.match(/\.(mp3|aac|m4a|amr|ogg|wav)$/)) {
                                mediaType = 'audio';
                              } else {
                                mediaType = 'document';
                              }
                            }

                            // Debug: Log media info for troubleshooting
                            if (msg.media_url) {
                              console.log('📷 Media info:', {
                                media_url: msg.media_url,
                                mediaUrl,
                                media_type: msg.media_type,
                                detectedType: mediaType,
                                hasMediaUrl: !!mediaUrl,
                                hasMediaType: !!mediaType
                              });
                            }

                            return (
                              <div key={getMessageRenderKey(msg, `${group.dateKey}-${index}`)} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                                <div className={`px-4 py-3 rounded-2xl max-w-xs lg:max-w-md break-words shadow-sm ${isOutgoing
                                  ? 'text-white rounded-br-md'
                                  : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                                  }`} style={isOutgoing ? { backgroundColor: '#042b12' } : {}}>

                                  {/* Display Media */}
                                  {mediaUrl && (
                                    <div className="mb-2">
                                      {/* Images - check by mediaType or file extension */}
                                      {(mediaType === 'image' || mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                                        <div className="relative group">
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block cursor-pointer"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              window.open(mediaUrl, '_blank');
                                            }}
                                          >
                                            <img
                                              src={mediaUrl}
                                              alt={msg.message_text || 'Image'}
                                              className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
                                              onError={(e) => {
                                                console.error('❌ Failed to load image:', mediaUrl);
                                                // Hide image and show fallback
                                                e.target.style.display = 'none';
                                                const container = e.target.closest('.relative');
                                                if (container) {
                                                  const fallback = container.querySelector('.image-fallback');
                                                  if (fallback) {
                                                    fallback.style.display = 'flex';
                                                  }
                                                }
                                              }}
                                              title="Click to open image in new tab"
                                            />
                                          </a>
                                          {/* Fallback link if image fails to load */}
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="image-fallback hidden items-center gap-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer group"
                                            style={{ display: 'none' }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              window.open(mediaUrl, '_blank');
                                            }}
                                          >
                                            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium text-gray-700 truncate group-hover:text-blue-600 transition-colors">
                                                Image
                                              </p>
                                              <p className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors">
                                                Click to open in new tab
                                              </p>
                                            </div>
                                            <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                          </a>
                                          {/* Hover overlay - make it clickable too */}
                                          <div
                                            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(mediaUrl, '_blank');
                                            }}
                                          >
                                            <span className="text-white text-xs font-medium bg-black bg-opacity-50 px-2 py-1 rounded">
                                              Click to open
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {(mediaType === 'video' || (!mediaType && mediaUrl.match(/\.(mp4|3gp|webm)$/i))) && (
                                        <div className="relative group">
                                          <video
                                            src={mediaUrl}
                                            controls
                                            className="max-w-full h-auto rounded-lg"
                                            style={{ maxHeight: '300px' }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs hover:bg-opacity-70 transition-all"
                                            title="Open video in new tab"
                                          >
                                            Open
                                          </a>
                                        </div>
                                      )}
                                      {/* Audio - check by mediaType or file extension */}
                                      {(mediaType === 'audio' || mediaUrl.match(/\.(mp3|aac|m4a|amr|ogg|wav)$/i)) && (
                                        <div className="relative">
                                          <audio
                                            src={mediaUrl}
                                            controls
                                            className="w-full"
                                          >
                                            Your browser does not support the audio tag.
                                          </audio>
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs hover:bg-opacity-70 transition-all"
                                            title="Open audio in new tab"
                                          >
                                            Open
                                          </a>
                                        </div>
                                      )}
                                      {/* Documents - everything else that's not image/video/audio */}
                                      {(mediaType === 'document' || (!mediaType && !mediaUrl.match(/\.(jpg|jpeg|png|gif|webp|mp4|3gp|webm|mp3|aac|m4a|amr|ogg|wav)$/i))) && (
                                        <a
                                          href={mediaUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer group"
                                          title="Click to open document in new tab"
                                        >
                                          <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-700 truncate group-hover:text-blue-600 transition-colors">
                                              {msg.message_text || 'Document'}
                                            </p>
                                            <p className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors">
                                              Click to open in new tab
                                            </p>
                                          </div>
                                          <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                  )}

                                  {/* Display Text (caption or message) */}
                                  {msg.message_text && (
                                    <p className="text-sm leading-relaxed">{msg.message_text}</p>
                                  )}

                                  {/* Display placeholder for media without caption */}
                                  {mediaUrl && !msg.message_text && !mediaType && (
                                    <p className="text-sm leading-relaxed opacity-75">Media message</p>
                                  )}

                                  <div className={`flex items-center justify-end gap-1 mt-2 ${isOutgoing ? 'text-white' : 'text-gray-500'}`}>
                                    <span className={`text-xs ${isOutgoing ? 'text-white/90' : 'text-gray-500'}`}>
                                      {formatLocalTime(msg.created_at, 'time-ampm')}
                                    </span>
                                    {tickIcon && (
                                      <span className={tickColor}>
                                        {tickIcon}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="text-center text-gray-500 mt-8">
                      <MessageCircle className="mx-auto h-8 w-8 text-gray-300" />
                      <p className="mt-2">No messages yet</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="shrink-0 px-3 py-3 sm:px-4 sm:py-4 border-t border-gray-200 bg-white">
              {/* 24-Hour Window Status */}
              {selectedChat && (
                <div className={`mb-2 px-3 py-2 rounded-lg border flex items-center gap-2 ${windowStatus.isActive ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'
                  }`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${windowStatus.isActive ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-xs font-medium text-gray-700">
                    {windowStatus.isActive ? '24h Window Active' : '24h Window Deactive'}
                  </span>
                </div>
              )}

              {/* Template dropdown (when opened from icon in input bar) */}
              {showTemplateDropdown && (
                <div className="mb-2">
                  <select
                    value={selectedTemplate ? (selectedTemplate.id || selectedTemplate.name) : ''}
                    onChange={(e) => {
                      const templateId = e.target.value;
                      const template = facebookTemplates.find(t =>
                        (t.id && t.id.toString() === templateId) || t.name === templateId
                      );
                      if (template) {
                        handleTemplateSelect(template);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">Select a WhatsApp template...</option>
                    {facebookTemplates.map((template) => (
                      <option key={template.id || template.name} value={template.id || template.name}>
                        {template.name}
                        {template.status && ` [${template.status}]`}
                        {template.language && ` (${template.language})`}
                      </option>
                    ))}
                  </select>
                  {facebookTemplates.length === 0 && (
                    <div className="mt-2">
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                        <p className="text-xs text-yellow-800 mb-2">
                          No WhatsApp templates cached yet.
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            setSyncingTemplates(true);
                            try {
                              const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                              toast.loading('Syncing WhatsApp templates...');
                              const syncResponse = await apiClient.post('/api/whatsapp/templates/sync', {}, { headers });
                              if (syncResponse.data.success) {
                                toast.dismiss();
                                toast.success(`Synced ${syncResponse.data.saved} templates successfully`);
                                await queryClient.invalidateQueries(['chat-templates', currentWorkspace?.id]);
                              }
                            } catch (error) {
                              toast.dismiss();
                              toast.error(error.response?.data?.error || 'Failed to sync templates');
                            } finally {
                              setSyncingTemplates(false);
                            }
                          }}
                          disabled={syncingTemplates}
                          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50 transition-all shadow-md"
                        >
                          <RefreshCw className={`h-4 w-4 ${syncingTemplates ? 'animate-spin' : ''}`} />
                          <span>{syncingTemplates ? 'Syncing...' : 'Sync Templates from Meta'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Template Parameters */}
              {selectedTemplate && Object.keys(templateParams).length > 0 && (
                <div className="mb-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Template Parameters:</p>
                  {Object.keys(templateParams).map(param => (
                    <input
                      key={param}
                      type="text"
                      value={templateParams[param]}
                      onChange={(e) => setTemplateParams({
                        ...templateParams,
                        [param]: e.target.value
                      })}
                      placeholder={`Enter ${param}`}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                    />
                  ))}
                </div>
              )}


              {/* Template Preview */}
              {renderTemplatePreview()}

              {/* Media Preview */}
              {mediaPreview && (
                <div className="px-4 pt-2 pb-0">
                  <div className="relative inline-block max-w-xs">
                    {mediaPreview.type.startsWith('image/') ? (
                      <img
                        src={mediaPreview.url}
                        alt="Preview"
                        className="max-w-full max-h-48 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                        <FileText className="h-8 w-8 text-gray-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">
                            {selectedMedia?.name || 'File'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(selectedMedia?.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setSelectedMedia(null);
                        setMediaPreview(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Message Input */}
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                  disabled={!windowStatus.canSendFacebookTemplate}
                  className={`p-3 rounded-2xl transition-colors shrink-0 ${showTemplateDropdown
                    ? 'bg-blue-100 text-blue-700'
                    : !windowStatus.canSendFacebookTemplate
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  title={windowStatus.canSendFacebookTemplate ? 'Send WhatsApp template' : 'Templates unavailable'}
                >
                  <FileText className="h-5 w-5" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Check 24-hour window
                      if (!windowStatus.canSendDirect && !useTemplate) {
                        toast.error('Cannot attach media: 24-hour window expired. Only Facebook templates are allowed.');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        return;
                      }
                      // Check file size (16MB limit)
                      if (file.size > 16 * 1024 * 1024) {
                        toast.error('File size must be less than 16MB');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        return;
                      }
                      setSelectedMedia(file);
                      // Create preview URL
                      if (file.type.startsWith('image/')) {
                        setMediaPreview({
                          type: file.type,
                          url: URL.createObjectURL(file)
                        });
                      } else {
                        setMediaPreview({
                          type: file.type,
                          url: null
                        });
                      }
                    }
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!windowStatus.canSendDirect && !useTemplate) {
                      toast.error('Cannot attach media: 24-hour window expired. Only Facebook templates are allowed.');
                      return;
                    }
                    fileInputRef.current?.click();
                  }}
                  disabled={!windowStatus.canSendDirect && !useTemplate}
                  className={`p-3 rounded-2xl transition-colors shrink-0 ${!windowStatus.canSendDirect && !useTemplate
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  title={!windowStatus.canSendDirect && !useTemplate ? "24-hour window expired - Cannot attach media" : "Attach file"}
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={
                    !windowStatus.canSendDirect
                      ? "Only WhatsApp templates allowed - 24h window expired"
                      : useTemplate
                        ? "Or type a regular message..."
                        : "Type a message..."
                  }
                  className={`flex-1 px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${!windowStatus.canSendDirect
                    ? 'border-red-300 bg-red-50 text-red-500 cursor-not-allowed'
                    : 'border-gray-200 bg-gray-50'
                    }`}
                  disabled={(!windowStatus.canSendDirect && !useTemplate) || (useTemplate && selectedTemplate)}
                />
                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={
                    sending ||
                    sendInProgress ||
                    (!message.trim() && !selectedTemplate && !selectedMedia) ||
                    (!windowStatus.canSendDirect && !useTemplate && !selectedTemplate) ||
                    (selectedMedia && !windowStatus.canSendDirect && !useTemplate)
                  }
                  className={`p-3 rounded-2xl transition-colors shadow-sm flex items-center justify-center min-w-[44px] ${(!windowStatus.canSendDirect && !useTemplate && !selectedTemplate) ||
                    (selectedMedia && !windowStatus.canSendDirect && !useTemplate)
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    }`}
                >
                  {sending ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No chat selected</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Choose a chat from the list to start messaging and view conversation history
              </p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Chats;

